import { AfterViewInit, Component, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { InputTextModule } from "@openng/optimus-ui/inputtext";

import { FileSelectEvent, FileUpload, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { MessageService } from "@openng/optimus-ui/api";
import * as L from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import { Location } from "../../../../../models/api/asset/location";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { MediaService } from "../../../../../services/api/media/media.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { concatMap, first, Observable, of } from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { COUNTRY_OPTIONS } from "../../../../../constants/countryOptions";
import { CacheService } from "../../../../../services/cache/cache.service";
import { HasPermissionDirective } from "../../../../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../../../services/api/permission/permission.service";
import { DropdownOption } from "../../../../../models/ui/dropdownOption";
import { LocationDTO } from "../../../../../models/api/asset/location";
import { Media } from "../../../../../models/api/media";
import { SupportedLang } from "../../../../../models/ui/supportedLangs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMAIL_PATTERN } from "../../../../../constants/regExp";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { SelectModule } from "@openng/optimus-ui/select";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TimeoutComponent } from "../../../../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../../../../shared/utils/timeout.utils";
import { CommonUtilsService } from "../../../../../services/utils/common-utils/common-utils.service";

@Component({
  standalone: true,
  selector: "app-location",
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TranslateModule,
    InputTextModule,
    FileUploadModule,
    TextareaModule,
    SelectModule,
    HasPermissionDirective,
    ProgressSpinnerModule,
    TimeoutComponent,
  ],
  templateUrl: "./location.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./location.component.scss",
})
export class LocationComponent implements OnInit, AfterViewInit, OnDestroy {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  public translate = inject(TranslateService);
  private message = inject(MessageService);
  public permissions = inject(PermissionService);
  private mediaService = inject(MediaService);
  private assetService = inject(AssetService);
  private cache = inject(CacheService);
  private commonUtils = inject(CommonUtilsService);
  public location: Location = this.config.data.asset;

  public form = new FormGroup({
    label: new FormControl(this.location.label, Validators.required),
    email: new FormControl(this.location.email, [Validators.pattern(EMAIL_PATTERN)]),
    phone: new FormControl(this.location.phone),
    notes: new FormControl(this.location.notes),
    address: new FormGroup({
      street: new FormControl(this.location.address?.street, Validators.required),
      number: new FormControl(this.location.address?.number, Validators.required),
      zipCode: new FormControl(this.location.address?.zipCode, Validators.required),
      city: new FormControl(this.location.address?.city, Validators.required),
      country: new FormControl(this.location.address?.country, Validators.required),
    }),
    position: new FormGroup({
      latitude: new FormControl(this.location.position?.latitude, Validators.required),
      longitude: new FormControl(this.location.position?.longitude, Validators.required),
    }),
  });

  private map!: L.Map;
  private marker!: L.Marker;
  private icon!: L.Icon;
  private tileLayer!: L.TileLayer;

  public requestStarted = signal(false);
  public timeoutRequest = createTimeoutSignal(this.requestStarted);

  public countries: DropdownOption[] = COUNTRY_OPTIONS;

  private actionType!: "add" | "edit";
  public selectedFloorMap!: File | null;
  private mediaFile!: Media;

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: (langChange) => {
        const newLang = langChange.lang as SupportedLang;

        this.changeTiles(newLang);
      },
    });
  }

  public ngOnInit() {
    if (this.config.data.asset) {
      this.checkMedia();
    }

    if (this.config.data.actionType) {
      this.actionType = this.config.data.actionType;
    }
  }

  public ngAfterViewInit() {
    this.initMap();
  }

  public ngOnDestroy() {
    this.map?.remove();
  }

  /**
   * Clears the selected file from the file upload component when the upload button is clicked.
   * @param fileupload The FileUpload component from which to clear the selected file.
   */
  public onFileUploadClick(fileupload: FileUpload) {
    this.selectedFloorMap = null;
    fileupload.clear();
  }

  /**
   * Fetches the mediafile for the floor map url if it exists
   * and assigns it to the mediaFile property
   */
  private checkMedia() {
    if (this.location.id && this.location.floorMapUrl) {
      this.mediaService.getMedia(ASSET_TYPES.LOCATION, this.location.id, "FLOOR_MAP").subscribe((media: Media[]) => {
        if (media) {
          const matchingMediaFile = media.find((mediaFile: Media) => mediaFile.url === this.location.floorMapUrl);
          if (matchingMediaFile) {
            this.mediaFile = matchingMediaFile;
          }
        }
      });
    }
  }

  /**
   * Initializes the map and sets up the necessary configuration.
   */
  private initMap() {
    this.map = L.map("locationMapContainer", environment.mapConfig).setView([51.1642292, 10.4541194], 6);
    const currentLang: SupportedLang = this.translate.currentLang as SupportedLang;

    const tilesUrl = environment.mapTileUrl[currentLang];
    if (tilesUrl) {
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      const provider = new OpenStreetMapProvider();
      const searchControl = GeoSearchControl({
        provider: provider,
        style: "bar",
        searchLabel: this.translate.instant("common.enter_address"),
        updateMap: true,
        autoClose: true,
      });
      this.icon = this.initIcon();
      this.initMarker();
      this.initClickMapEvent();

      this.map.addControl(searchControl);
    }
  }

  /**
   * Initializes a marker on the map with the given latitude and longitude.
   */
  private initMarker() {
    const position = this.location.position;
    if (position) {
      const lat = position.latitude;
      const lng = position.longitude;
      if (lat && lng) {
        this.marker = L.marker([lat, lng], {
          icon: this.icon,
        }).addTo(this.map);
      }
    }
  }

  /**
   * Initializes the click event on the map.
   */
  private initClickMapEvent() {
    this.map.on("click", (e: L.LeafletMouseEvent) => {
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      if (!this.marker) {
        this.marker = L.marker([lat, lng], {
          icon: this.icon,
        }).addTo(this.map);
      } else {
        const newLatLng = new L.LatLng(lat, lng);
        this.marker.setLatLng(newLatLng);
      }
      this.form?.controls["position"].patchValue({
        latitude: lat,
        longitude: lng,
      });
    });
  }

  /**
   * Initializes an icon for a map library.
   * @return {L.icon} The initialized icon.
   */
  private initIcon() {
    return L.icon({
      iconSize: [25, 34], // size of the icon
      iconAnchor: [12.5, 25],
      iconUrl: "assets/icons/map_marker_ok.svg",
      popupAnchor: [0, -30],
    });
  }

  /**
   * A method to save the location details based on the form values.
   */
  public save(): void {
    const locationFormDTO = this.createLocationDTO();

    if (locationFormDTO && this.form?.valid) {
      if (!this.requestStarted()) {
        this.location.properties = locationFormDTO;
        this.saveLocation();
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("common.invalid_form"),
        life: 4500,
      });
    }
  }

  private createLocationDTO(): LocationDTO | null {
    const locationFormValues = this.form?.value;
    if (locationFormValues && locationFormValues.label) {
      const location: LocationDTO = {
        ...locationFormValues,
        label: locationFormValues.label,
        position: this.commonUtils.normalizeLatLng(locationFormValues.position),
      };
      return location;
    } else {
      return null;
    }
  }

  private afterServerResponse() {
    return {
      next: () => {
        this.requestStarted.set(false);
        if (this.actionType === "edit") {
          this.cache.delete(environment.baseUrl + `/asset/${this.location.id}?assetType=LOCATION`);
        }
        this.ref.close(true);
      },
      error: () => {
        this.requestStarted.set(false);
      },
    };
  }

  /**
   * A method to add a new location to the server. If a floor map was selected,
   * it uploads the floor map and edits the location with the floor map url.
   * If no floor map was selected, it only adds the location.
   */
  private addLocation() {
    const directParent = this.location.directParentId;
    const createLocation$ = this.assetService
      .createAsset(ASSET_TYPES.LOCATION, this.location, directParent)
      .pipe(first());

    if (this.selectedFloorMap) {
      // If file selected following steps: create location, upload floor map, edit location with floor map url
      createLocation$
        .pipe(
          concatMap((locationId: string | null) => {
            if (locationId === null) return of(null);
            this.location.id = locationId;
            return this.uploadFloorMap();
          }),
          concatMap((floorMapRes: Media[] | null) => {
            if (floorMapRes) {
              this.location.floorMapUrl = floorMapRes[0].url;
              return this.assetService.editAsset(ASSET_TYPES.LOCATION, this.location);
            } else {
              return of(null);
            }
          })
        )
        .subscribe(this.afterServerResponse());
    } else {
      createLocation$.subscribe(this.afterServerResponse());
    }
  }

  /**
   * Edits a location.
   * If a new floor map was selected, edits the existing file or uploads a new one.
   * If no floor map was selected, it only edits the location.
   */
  private editLocation() {
    // File was selected by user:
    if (this.selectedFloorMap) {
      // File existed before edit:
      if (this.mediaFile && this.mediaFile.id) {
        const editFile$ = this.mediaService
          .editMedia(this.mediaFile.id, this.selectedFloorMap, "floorMap")
          .pipe(first());
        editFile$
          .pipe(
            concatMap((editFileRes: Media) => {
              this.location.floorMapUrl = editFileRes.url;
              return this.assetService.editAsset(ASSET_TYPES.LOCATION, this.location).pipe(first());
            })
          )
          .subscribe(this.afterServerResponse());
        // File didnt exist before:
      } else {
        const uploadFile$ = this.uploadFloorMap();
        uploadFile$
          .pipe(
            concatMap((uploadFileRes: Media[] | null) => {
              if (uploadFileRes && uploadFileRes[0].url) {
                this.location.floorMap = uploadFileRes[0].url;
              }

              return this.assetService.editAsset(ASSET_TYPES.LOCATION, this.location).pipe(first());
            })
          )
          .subscribe(this.afterServerResponse());
      }
    } else {
      this.assetService.editAsset(ASSET_TYPES.LOCATION, this.location).subscribe(this.afterServerResponse());
    }
  }

  /**
   * Calls either addLocation or editLocation based on actionType.
   * Sets requestStarted to true before calling.
   */
  private saveLocation() {
    this.requestStarted.set(true);
    if (this.actionType === "add") {
      this.addLocation();
    } else if (this.actionType === "edit") {
      this.editLocation();
    }
  }

  /**
   * Uploads the selected floor map for the current location to the server.
   * @returns {Observable<Media[] | null>} Observable of uploaded media or null if no location ID.
   */
  private uploadFloorMap(): Observable<Media[] | null> {
    if (this.location.id) {
      return this.mediaService
        .uploadMedia(ASSET_TYPES.LOCATION, this.location.id, "FLOOR_MAP", this.selectedFloorMap, "floorMap")
        .pipe(first());
    } else {
      return of(null);
    }
  }

  /**
   * Closes the dialog.
   */
  public closeDialog(): void {
    this.ref.close();
  }

  /**
   * Called when the user selects a new floor map to upload.
   * @param event The event that triggered the function call, containing the selected file.
   */
  public onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    this.selectedFloorMap = file;
  }

  /**
   * Changes the map tiles to match the specified language.
   * Removes the current tile layer and adds a new one based on the new language.
   * Invalidates the map size to ensure proper rendering after changing tiles.
   *
   * @param newLang The new language for which to update the map tiles.
   */
  private changeTiles(newLang: SupportedLang) {
    if (this.map && this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
      const tilesUrl = environment.mapTileUrl[newLang];
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 50);
    }
  }
}
